import React from 'react';
import PrintLayout from './PrintLayout';

const PEOPLE_PER_PAGE = 13;

// Keys match BACKTICK_CODES in shortcodes.js (e.g. '`w', '`g')
const GENIUS_DEFS = [
  { code: '`w', label: 'W', fullLabel: 'Wonder',      color: '#cba43a' },
  { code: '`i', label: 'I', fullLabel: 'Invention',   color: '#5f7942' },
  { code: '`d', label: 'D', fullLabel: 'Discernment', color: '#2a3d5b' },
  { code: '`g', label: 'G', fullLabel: 'Galvanizing', color: '#922b3f' },
  { code: '`e', label: 'E', fullLabel: 'Enablement',  color: '#4cc9f0' },
  { code: '`t', label: 'T', fullLabel: 'Tenacity',    color: '#e76f51' },
];

function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

function darken(hex) {
  const r = Math.max(0, parseInt(hex.slice(1, 3), 16) - 60);
  const g = Math.max(0, parseInt(hex.slice(3, 5), 16) - 60);
  const b = Math.max(0, parseInt(hex.slice(5, 7), 16) - 60);
  return `rgb(${r},${g},${b})`;
}

function chunk(arr, size) {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks.length ? chunks : [[]];
}

const LeftPanel = ({ isFirst }) => (
  <div style={{
    width: '32%', background: '#1e2e45',
    padding: '6% 5%', display: 'flex', flexDirection: 'column',
  }}>
    <div style={{
      fontSize: 9, fontWeight: 700, letterSpacing: '2px', color: '#5f7942',
      textTransform: 'uppercase', marginBottom: 10, fontFamily: 'Montserrat, sans-serif',
    }}>
      Right Chemistry
    </div>
    <div style={{
      fontFamily: "'Playfair Display', Georgia, serif",
      fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1.2, marginBottom: 14,
    }}>
      {isFirst ? 'The Right Chemistry' : 'Right Chemistry (cont.)'}
    </div>
    {isFirst && (
      <>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.75 }}>
          The ideal Tactical meeting centers on Galvanizing, Enablement, and Tenacity. It assumes the team has conducted a good offsite and confirmed their Thematic Goal.
        </div>
        <div style={{
          marginTop: 16, padding: '10px 12px',
          borderLeft: '2px solid #5f7942',
          fontSize: 10, fontStyle: 'italic',
          color: 'rgba(255,255,255,0.5)', lineHeight: 1.65,
        }}>
          "We've identified our strategic focus, and we are inspiring and pushing people to execute. This is a G/E/T meeting."
          <div style={{ marginTop: 4, fontStyle: 'normal', fontWeight: 600, color: 'rgba(255,255,255,0.3)' }}>
            — Working Genius on Productive Meetings
          </div>
        </div>
      </>
    )}
  </div>
);

export default function PrintGenius({ pageNumber, reportData, chartOnly }) {
  const bySpeaker = reportData?.genius?.bySpeaker || {};
  const people = Object.entries(bySpeaker);
  const pages = chunk(people, PEOPLE_PER_PAGE);

  return pages.map((pagepeople, pi) => (
    <PrintLayout key={pi} pageNumber={pi === 0 ? pageNumber : null}>
      <div style={{ position: 'absolute', top: 0, bottom: 68, left: 0, right: 0, display: 'flex' }}>
        {!chartOnly && <LeftPanel isFirst={pi === 0} />}

        <div style={{ flex: 1, background: '#f5f4f0', padding: '4% 5% 3%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '1.5px', color: '#6b757e',
            textTransform: 'uppercase', marginBottom: 12, fontFamily: 'Montserrat, sans-serif',
          }}>
            Right Chemistry by Person
          </div>

          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, paddingLeft: 110 }}>
            {GENIUS_DEFS.map(({ label, color }) => (
              <div key={label} style={{
                flex: 1, textAlign: 'center',
                background: color, color: 'white',
                borderRadius: 4, padding: '4px 0',
                fontSize: 12, fontWeight: 800,
                marginRight: 4,
              }}>
                {label}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pagepeople.map(([name, counts]) => {
              const maxCount = Math.max(...GENIUS_DEFS.map(d => counts[d.code] || 0), 1);
              return (
                <div key={name} style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{
                    width: 110, fontSize: 10, fontWeight: 600,
                    color: '#1e2e45', flexShrink: 0, paddingRight: 8,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {name.split('@')[0]}
                  </div>
                  {GENIUS_DEFS.map(({ code, color }) => {
                    const count = counts[code] || 0;
                    const intensity = count / maxCount;
                    return (
                      <div key={code} style={{
                        flex: 1, marginRight: 4, height: 28,
                        background: count > 0
                          ? `rgba(${hexToRgb(color)}, ${0.15 + intensity * 0.75})`
                          : '#ececed',
                        borderRadius: 4,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700,
                        color: count > 0 ? darken(color) : '#aaa',
                      }}>
                        {count > 0 ? count : ''}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* Legend centered in off-white area below chart */}
          {chartOnly ? (
            <div style={{ marginTop: 'auto', paddingTop: 14, borderTop: '1px solid rgba(42,61,91,0.12)', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px 12px' }}>
              {GENIUS_DEFS.map(({ label, fullLabel, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ background: color, color: 'white', borderRadius: 4, width: 20, height: 20, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                    {label}
                  </div>
                  <span style={{ fontSize: 9, color: '#4a5568' }}>{fullLabel}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', justifyContent: 'center', gap: 20, borderTop: '1px solid rgba(42,61,91,0.12)' }}>
              {GENIUS_DEFS.map(({ label, fullLabel, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ background: color, color: 'white', borderRadius: 4, width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
                    {label}
                  </div>
                  <span style={{ fontSize: 9, color: '#4a5568' }}>{fullLabel}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PrintLayout>
  ));
}
